export type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

export type CalloutPlacement = 'above' | 'below' | 'left' | 'right';

export type CalloutLayout = {
  top: number;
  left: number;
  placement: CalloutPlacement;
  maxWidth: number;
};

const PAD = 12;
const SPOTLIGHT_PAD = 8;
const DEFAULT_CALLOUT_W = 320;
const DEFAULT_CALLOUT_H = 220;

export function inflateRect(rect: Rect, pad = SPOTLIGHT_PAD): Rect {
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
    bottom: rect.bottom + pad,
    right: rect.right + pad,
  };
}

export function rectFromDOMRect(r: DOMRect | DOMRectReadOnly): Rect {
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    bottom: r.bottom,
    right: r.right,
  };
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Place a callout near a spotlight hole, preferring below then above, then
 * sides. Edge-aware for desktop/tablet viewports.
 */
export function placeCallout(
  hole: Rect,
  viewport: { width: number; height: number },
  preferred: CalloutPlacement = 'below',
  calloutSize: { width: number; height: number } = {
    width: DEFAULT_CALLOUT_W,
    height: DEFAULT_CALLOUT_H,
  },
): CalloutLayout {
  const { width: cw, height: ch } = calloutSize;
  const maxWidth = Math.min(cw, viewport.width - PAD * 2);

  const candidates: CalloutPlacement[] = [
    preferred,
    'below',
    'above',
    'right',
    'left',
  ].filter((v, i, a) => a.indexOf(v) === i) as CalloutPlacement[];

  const needed = (p: CalloutPlacement): number =>
    p === 'below' || p === 'above' ? ch + PAD : maxWidth + PAD;

  const space = (p: CalloutPlacement): number =>
    p === 'below'
      ? viewport.height - hole.bottom - PAD
      : p === 'above'
        ? hole.top - PAD
        : p === 'right'
          ? viewport.width - hole.right - PAD
          : hole.left - PAD;

  // Prefer the requested side when it fits; otherwise pick the roomiest side.
  let best = candidates[0]!;
  if (space(preferred) >= needed(preferred)) {
    best = preferred;
  } else {
    let bestScore = -Infinity;
    for (const p of candidates) {
      const s = space(p) - needed(p);
      if (s > bestScore) {
        bestScore = s;
        best = p;
      }
    }
  }

  let top = 0;
  let left = 0;

  if (best === 'below') {
    top = hole.bottom + PAD;
    left = hole.left + hole.width / 2 - maxWidth / 2;
  } else if (best === 'above') {
    top = hole.top - ch - PAD;
    left = hole.left + hole.width / 2 - maxWidth / 2;
  } else if (best === 'right') {
    top = hole.top + hole.height / 2 - ch / 2;
    left = hole.right + PAD;
  } else {
    top = hole.top + hole.height / 2 - ch / 2;
    left = hole.left - maxWidth - PAD;
  }

  left = clamp(left, PAD, Math.max(PAD, viewport.width - maxWidth - PAD));
  top = clamp(top, PAD, Math.max(PAD, viewport.height - ch - PAD));

  return { top, left, placement: best, maxWidth };
}

export function findFtueAnchor(anchor: string): HTMLElement | null {
  if (typeof document === 'undefined' || !anchor) return null;
  try {
    return document.querySelector(`[data-ftue="${CSS.escape(anchor)}"]`);
  } catch {
    return document.querySelector(`[data-ftue="${anchor}"]`);
  }
}

/** Max spotlight height as a fraction of the viewport (tall tables/sections). */
export const FTUE_SPOTLIGHT_MAX_VH = 0.52;

export function measureAnchor(
  anchor: string,
  opts?: { maxHeight?: number },
): Rect | null {
  const el = findFtueAnchor(anchor);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  const full = rectFromDOMRect(r);
  const maxHeight = opts?.maxHeight;
  if (maxHeight != null && full.height > maxHeight) {
    return {
      top: full.top,
      left: full.left,
      width: full.width,
      height: maxHeight,
      bottom: full.top + maxHeight,
      right: full.right,
    };
  }
  return full;
}

/** Nearest scrollable ancestor (Ops Command canvas, drill body, etc.). */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (typeof document === 'undefined' || !el) return null;
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  const canvas = document.querySelector('.ops-command .canvas');
  return canvas instanceof HTMLElement ? canvas : null;
}

export function getFtueScrollBehavior(
  media:
    | Pick<Window, 'matchMedia'>
    | undefined = typeof window === 'undefined' ? undefined : window,
): ScrollBehavior {
  try {
    return media?.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
  } catch {
    return 'smooth';
  }
}

/**
 * Scroll the anchored element into a useful band: top of the target near the
 * upper third of the scrollport so tall sections (outlet table) aren't stuck
 * as a one-line sliver at the bottom edge.
 */
export function ensureAnchorVisible(anchor: string): void {
  const el = findFtueAnchor(anchor);
  if (!el || typeof window === 'undefined') return;

  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || 800;
  const behavior = getFtueScrollBehavior();
  const minTop = 64;
  const idealTop = Math.min(120, Math.round(vh * 0.16));
  const visibleBand = Math.min(r.height, vh * FTUE_SPOTLIGHT_MAX_VH);
  const enoughVisible =
    r.top >= minTop - 8 &&
    r.top <= idealTop + 40 &&
    r.top + visibleBand <= vh - 24;
  if (enoughVisible) return;

  const scroller = findScrollParent(el);
  if (scroller) {
    const scrollerTop = scroller.getBoundingClientRect().top;
    const delta = r.top - scrollerTop - idealTop;
    const next = Math.max(0, scroller.scrollTop + delta);
    try {
      scroller.scrollTo({ top: next, behavior });
    } catch {
      scroller.scrollTop = next;
    }
    return;
  }

  try {
    el.scrollIntoView({ block: 'start', inline: 'nearest', behavior });
  } catch {
    el.scrollIntoView();
  }
}

/** Apply a wheel delta to the scroll parent under an FTUE anchor (or canvas). */
export function scrollFtueBackdrop(anchor: string | null | undefined, deltaY: number): void {
  if (!deltaY || typeof document === 'undefined') return;
  const el = anchor ? findFtueAnchor(anchor) : null;
  const scroller =
    findScrollParent(el) ??
    (document.querySelector('.ops-command .canvas') as HTMLElement | null);
  if (!scroller) return;
  scroller.scrollTop += deltaY;
}
